import React, { useRef } from 'react';
import { CalendarClock, Plus, Trash2 } from 'lucide-react';
import MergeTagChips from '../../../components/marketing/MergeTagChips.jsx';
import { toParts, fromParts, describeOffset } from '../../../lib/retentionOffsets.js';

export const DEFAULT_RETENTION = Object.freeze({ sendTime: '10:00', milestones: [] });
const NEW_TOUCH = Object.freeze({ label: '', days: 30, months: 0, body: '' });

const field = 'bg-civix-panelLight border border-white/10 rounded-lg px-2.5 py-1.5 text-sm text-white';

function Touch({ m, index, onChange, onRemove, canRemove }) {
  const ref = useRef(null);
  const p = toParts(m);
  const setPart = (patch) => {
    const next = { ...p, ...patch };
    if (next.unit === 'months') next.direction = 'after'; // months only count forward from the draft
    const off = fromParts(next);
    onChange({ ...m, ...off, label: describeOffset(off) });
  };
  return (
    <div className="rounded-lg border border-white/10 p-3 space-y-2" data-testid={`retention-touch-${index}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase tracking-wide text-white/40">Send</span>
        <input type="number" min="0" max={p.unit === 'months' ? 60 : 3650} value={p.amount} onChange={(e) => setPart({ amount: e.target.value })} aria-label="Amount" data-testid="retention-amount" className={`${field} w-20`} />
        <select value={p.unit} onChange={(e) => setPart({ unit: e.target.value })} aria-label="Unit" className={field}>
          <option value="days">days</option>
          <option value="months">months</option>
        </select>
        <select value={p.direction} onChange={(e) => setPart({ direction: e.target.value })} disabled={p.unit === 'months'} aria-label="Before or after" data-testid="retention-direction" className={`${field} disabled:opacity-60`}>
          <option value="before">before</option>
          <option value="after">after</option>
        </select>
        <span className="text-xs text-white/60">the first draft</span>
        <span className="ml-auto text-[11px] text-white/40">{describeOffset(m)}</span>
        <button type="button" onClick={onRemove} disabled={!canRemove} aria-label={`Remove touch ${index + 1}`} className="p-1.5 rounded text-rose-300 hover:bg-rose-500/10 disabled:opacity-30"><Trash2 className="w-4 h-4" /></button>
      </div>
      <textarea ref={ref} value={m.body} onChange={(e) => onChange({ ...m, body: e.target.value })} rows={3} maxLength={1600} placeholder="Message" data-testid="retention-body" className={`${field} w-full font-mono`} />
      <MergeTagChips value={m.body} onChange={(v) => onChange({ ...m, body: v })} textareaRef={ref} testId={`retention-chips-${index}`} />
    </div>
  );
}

/**
 * Editor for a Sold Retention drip: a few texts anchored to the policy's first draft date.
 * @param {{retention: {sendTime: string, milestones: object[]}, onChange: (r: object) => void}} props
 */
export default function RetentionDripEditor({ retention, onChange }) {
  const set = (patch) => onChange({ ...retention, ...patch });
  const setTouch = (i, t) => set({ milestones: retention.milestones.map((x, j) => (j === i ? t : x)) });
  return (
    <div className="panel p-4 space-y-4" data-testid="retention-editor">
      <div className="flex items-center gap-2 font-semibold text-white"><CalendarClock className="w-4 h-4 text-civix-purple" /> Sold Retention Texts</div>
      <p className="text-xs text-white/60">
        Starts automatically when you mark a lead <b>Sold</b> and enter the first policy draft date. Each text is timed from that date.
        While this drip exists it replaces the built-in retention schedule. A reply from the client never stops it.
      </p>
      <label className="block text-[10px] uppercase tracking-wide text-white/40">
        Send time (client's local time)
        <input type="time" value={retention.sendTime} onChange={(e) => set({ sendTime: e.target.value || '10:00' })} data-testid="retention-send-time" className={`mt-1 block ${field}`} />
      </label>
      <div className="space-y-3">
        {retention.milestones.map((m, i) => (
          <Touch key={i} m={m} index={i} onChange={(t) => setTouch(i, t)} canRemove={retention.milestones.length > 1} onRemove={() => set({ milestones: retention.milestones.filter((_, j) => j !== i) })} />
        ))}
      </div>
      <button type="button" disabled={retention.milestones.length >= 12} onClick={() => set({ milestones: [...retention.milestones, { ...NEW_TOUCH, label: describeOffset(NEW_TOUCH) }] })} data-testid="retention-add" className="btn-secondary w-full flex items-center justify-center gap-1 text-sm disabled:opacity-40">
        <Plus className="w-4 h-4" /> Add a text
      </button>
    </div>
  );
}
